// BigCodeDisplay — large 6-digit invite code render with copy-to-clipboard.
//
// Per W2 §4.3 step 2 Sub-flow A "建立邀請連結" screen.  Renders the code
// in 6 monospace boxes (one per digit) plus a copy-as-text action.  Uses
// ``navigator.clipboard`` (per spec; falls back to a hidden textarea).

import { useState } from "react";
import { Check, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";

interface BigCodeDisplayProps {
  code: string;
  expiresAt?: string | null;
}

export function BigCodeDisplay({ code, expiresAt }: BigCodeDisplayProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-3 items-center"
      data-testid="big-code-display"
      data-code={code}
    >
      <div className="flex gap-2" aria-label="6 位邀請碼">
        {code.split("").map((digit, idx) => (
          <div
            key={`${digit}-${idx}`}
            data-testid={`big-code-digit-${idx}`}
            className={cn(
              "w-12 h-14 rounded-md bg-paper-deep border border-hairline",
              "flex items-center justify-center font-mono text-display-2 text-ink-1",
            )}
          >
            {digit}
          </div>
        ))}
      </div>
      <div className="flex gap-3">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleCopy}
          data-testid="copy-code"
          leadingIcon={
            copied ? (
              <Check className="w-3.5 h-3.5 text-you-strong" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )
          }
        >
          {copied ? "已複製" : "複製邀請碼"}
        </Button>
      </div>
      {expiresAt ? (
        <p className="text-body-sm text-ink-3" data-testid="code-expires">
          過期時間: {new Date(expiresAt).toLocaleString("zh-Hant")}
        </p>
      ) : null}
    </div>
  );
}
